import {
  Box,
  Typography,
  TextField,
  Chip,
} from '@mui/material';
import type { BpmnElement } from '../../hooks/useBpmnModeler';

interface GeneralPropertiesEditorProps {
  element: BpmnElement;
  onUpdateName: (name: string) => void;
  onUpdateDocumentation: (text: string) => void;
}

const ELEMENT_TYPE_LABELS: Record<string, string> = {
  'bpmn:StartEvent': 'Start-Ereignis',
  'bpmn:EndEvent': 'End-Ereignis',
  'bpmn:UserTask': 'Benutzer-Aufgabe',
  'bpmn:ServiceTask': 'Service-Aufgabe',
  'bpmn:ScriptTask': 'Skript-Aufgabe',
  'bpmn:BusinessRuleTask': 'Geschaeftsregel-Aufgabe',
  'bpmn:ManualTask': 'Manuelle Aufgabe',
  'bpmn:ReceiveTask': 'Empfangs-Aufgabe',
  'bpmn:SendTask': 'Sende-Aufgabe',
  'bpmn:CallActivity': 'Aufruf-Aktivitaet',
  'bpmn:SubProcess': 'Unterprozess',
  'bpmn:ExclusiveGateway': 'Exklusives Gateway (XOR)',
  'bpmn:ParallelGateway': 'Paralleles Gateway (AND)',
  'bpmn:InclusiveGateway': 'Inklusives Gateway (OR)',
  'bpmn:EventBasedGateway': 'Ereignisbasiertes Gateway',
  'bpmn:SequenceFlow': 'Sequenzfluss',
  'bpmn:MessageFlow': 'Nachrichtenfluss',
  'bpmn:DataStoreReference': 'Datenspeicher',
  'bpmn:DataObjectReference': 'Datenobjekt',
  'bpmn:IntermediateCatchEvent': 'Zwischenereignis (Empfang)',
  'bpmn:IntermediateThrowEvent': 'Zwischenereignis (Ausloesung)',
  'bpmn:BoundaryEvent': 'Rand-Ereignis',
  'bpmn:Process': 'Prozess',
  'bpmn:Participant': 'Teilnehmer (Pool)',
  'bpmn:Lane': 'Bahn (Lane)',
  'bpmn:TextAnnotation': 'Textanmerkung',
  'bpmn:Association': 'Assoziation',
};

const ELEMENT_TYPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  'bpmn:StartEvent': 'success',
  'bpmn:EndEvent': 'error',
  'bpmn:UserTask': 'primary',
  'bpmn:ServiceTask': 'secondary',
  'bpmn:ScriptTask': 'secondary',
  'bpmn:ExclusiveGateway': 'warning',
  'bpmn:ParallelGateway': 'warning',
  'bpmn:InclusiveGateway': 'warning',
  'bpmn:SequenceFlow': 'info',
};

export function GeneralPropertiesEditor({
  element,
  onUpdateName,
  onUpdateDocumentation,
}: GeneralPropertiesEditorProps) {
  const bo = element?.businessObject;
  const elementType = element?.type || '';

  const typeLabel = ELEMENT_TYPE_LABELS[elementType] || elementType.replace('bpmn:', '');
  const typeColor = ELEMENT_TYPE_COLORS[elementType] || 'default';

  // Get documentation text - ensure bo exists before accessing
  const documentation = bo?.documentation?.[0]?.text || '';

  if (!element || !bo) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Allgemein</Typography>
        <Chip
          label={typeLabel}
          size="small"
          color={typeColor as 'primary'}
          variant="outlined"
        />
      </Box>

      <TextField
        label="ID"
        size="small"
        fullWidth
        value={element.id}
        disabled
        helperText="Die Element-ID kann nicht geaendert werden"
      />

      <TextField
        label="Name"
        size="small"
        fullWidth
        value={bo.name || ''}
        onChange={(e) => onUpdateName(e.target.value)}
        placeholder="Elementname eingeben"
        helperText="Sichtbarer Name im Diagramm"
      />

      <TextField
        label="Dokumentation"
        size="small"
        fullWidth
        multiline
        rows={3}
        value={documentation}
        onChange={(e) => onUpdateDocumentation(e.target.value)}
        placeholder="Beschreibung des Elements..."
        helperText="Interne Dokumentation fuer Entwickler"
      />
    </Box>
  );
}
